import React, { useState } from 'react'
import {
    Button,
    ButtonProps,
    Flex,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
} from '@chakra-ui/react'
import { FaBan, FaCheck } from 'react-icons/fa6'
import { FiSlash } from 'react-icons/fi'

import { useActionItems } from '~contexts/action-item-provider'
import {
    namedOperations,
    PrescriptionRefillRequestFieldsFragment,
    PrescriptionRefillRequestType,
    useApprovePrescriptionRefillRequestMutation,
    useDenyPrescriptionRefillRequestMutation,
} from '~graphql/generated/graphql'
import RefillRequestConfirmModal from './refill-request-confirm-modal'

interface Props {
    refillRequest: PrescriptionRefillRequestFieldsFragment
    buttonOverrides?: ButtonProps
}

export default function RefillRequestActions({
    refillRequest,
    buttonOverrides,
}: Props) {
    const { actionItems, refetch } = useActionItems()

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [startOnDenial, setStartOnDenial] = useState(false)

    const actionItem = actionItems.find(
        item => item.prescription_refill_request?.id === refillRequest?.id,
    )

    const refetchQueries = [
        namedOperations.Query.queryPrescriptionRefillRequests,
        namedOperations.Query.countActionItems,
        namedOperations.Query.queryActionItems,
    ]

    const [approvePrescriptionRefillRequest, { loading: approveLoading }] =
        useApprovePrescriptionRefillRequestMutation({
            variables: {
                id: refillRequest?.id || '',
                actionItemId: actionItem?.id || '',
            },
            refetchQueries,
            onCompleted: () => refetch(),
            awaitRefetchQueries: true,
        })

    const [denyPrescriptionRefillRequest, { loading: denyLoading }] =
        useDenyPrescriptionRefillRequestMutation({
            refetchQueries,
            onCompleted: () => refetch(),
            awaitRefetchQueries: true,
        })

    async function handleApprove() {
        await approvePrescriptionRefillRequest()
        setIsModalOpen(false)
    }

    async function handleDeny(reason: string) {
        if (!reason.trim()) return
        await denyPrescriptionRefillRequest({
            variables: {
                id: refillRequest?.id || '',
                reason: reason.trim(),
                actionItemId: actionItem?.id || '',
            },
        })
        setIsModalOpen(false)
    }

    function openApproveModal() {
        setStartOnDenial(false)
        setIsModalOpen(true)
    }

    function openDenyModal() {
        setStartOnDenial(true)
        setIsModalOpen(true)
    }

    if (!refillRequest) {
        return null
    }

    const isRenewal =
        refillRequest.type === PrescriptionRefillRequestType.Renewal
    const requestTypeTitle = isRenewal ? 'Renewal' : 'Refill'

    return (
        <>
            <Flex gap={2} w="max-content">
                <Button
                    size="sm"
                    colorScheme="brand"
                    shadow="sm"
                    onClick={openApproveModal}
                    leftIcon={<FaCheck />}
                    {...buttonOverrides}
                >
                    Approve {requestTypeTitle}
                </Button>
                <Menu>
                    <MenuButton
                        as={IconButton}
                        size={buttonOverrides?.size ?? 'sm'}
                        icon={<FaBan />}
                        bg="red.100"
                        variant="outline"
                        colorScheme="danger"
                        border="none"
                        _hover={{ bg: 'red.200' }}
                        aria-label="More refill request options"
                    />
                    <MenuList py={0} boxShadow="2xl">
                        <MenuItem
                            onClick={e => {
                                e.stopPropagation()
                                openDenyModal()
                            }}
                            icon={<Icon as={FiSlash} boxSize="16px" />}
                            color="danger.600"
                        >
                            <Text fontSize="sm">Deny {requestTypeTitle}</Text>
                            <Text fontSize="11px" mt={-1}>
                                Requires confirmation
                            </Text>
                        </MenuItem>
                    </MenuList>
                </Menu>
            </Flex>

            <RefillRequestConfirmModal
                refillRequest={refillRequest}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onApprove={handleApprove}
                onDeny={handleDeny}
                isLoading={approveLoading || denyLoading}
                startOnDenial={startOnDenial}
            />
        </>
    )
}
